/**
 * OverlayGraphicPickerModal.tsx
 *
 * PLAT-10105 — picker for adding an overlay graphic to a clip from a
 * transcript selection. Surfaces ONLY `CompositionType.OVERLAY` templates
 * available to the user, split into Workspace Templates vs Arbor Templates via
 * `isGlobalPreset`. Picking a card calls `onSelect(template)`; the caller
 * binds it to the highlighted `[startTime, endTime]` range.
 *
 * Aspect-ratio rule (`currentAspectRatio`): an overlay tied to a specific
 * aspect ratio is shown only when it matches the target video's aspect
 * ratio; an overlay with no aspect ratio assigned is universal and always
 * shown. The caller passes the TARGET video's aspect ratio (the clip's, not
 * the global story's) because this modal renders outside the clip proxy.
 *
 * Within the active tab, overlays are grouped again — AI overlays first, then
 * plain graphics (see `isAIOverlay`). The AI/plain split is orthogonal to the
 * My/Arbor one, which is why it is a section inside each tab and not a third
 * chip: an AI overlay attached to the org would belong to both chips.
 *
 * Must be rendered inside a `CompositionProvider` (isQuoteCard=false) so
 * `useCompositionContext()` resolves. Original-language only — no
 * translation rows here.
 */

import { Chip, ChipGroup } from '@src/components/common/ChipGroup';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import SelectedCheckBadge from '@src/components/common/SelectedCheckBadge';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useCompositionContext } from '@src/components/sidepanel/CompositionContext';
import { CompositionType } from '@src/components/sidepanel/CompositionSettings/types';
import SparkleIcon from '@src/svgs/SparkleIcon';
import React, { useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import type { CompositionTemplate } from '@src/components/sidepanel/CompositionSettings/types';

const PICKER_TOOLTIP_ID = 'overlay-graphic-picker-tooltip';

const AI_BADGE_TOOLTIP =
  'Writes its text from your transcript — you can edit it after.';

/**
 * An overlay that generates its own copy: it carries at least one `aiText`
 * control, the same flag `AITextControl` keys the Generate affordance off.
 *
 * Deliberately the only test applied — where the composition came from (the
 * org's `compositionsBranding`, a video template, or a global Arbor preset) is
 * irrelevant, and all three arrive in the same `compositions` array anyway.
 */
const isAIOverlay = (composition: CompositionTemplate): boolean =>
  composition.controls.some(control => control.type === 'aiText');

interface OverlayGraphicPickerModalProps {
  onSelect: (template: CompositionTemplate) => void;
  onClose: () => void;
  /**
   * Target video's aspect ratio (e.g. `'16:9'`). Overlays assigned to a
   * specific aspect ratio show only when they match this; overlays with no
   * aspect ratio assigned always show. Pass the clip's aspect ratio.
   */
  currentAspectRatio: string;
}

export const OverlayGraphicPickerModal: React.FC<
  OverlayGraphicPickerModalProps
> = ({ onSelect, onClose, currentAspectRatio }) => {
  const { compositions, isLoading } = useCompositionContext();

  // Lock to overlay graphics only (the other composition subtypes —
  // intro/end/name/quote cards — are out of scope), then apply the
  // aspect-ratio rule: a tied overlay must match the target aspect ratio; an
  // untied overlay (no `aspectRatio`) is universal and always relevant.
  //
  // Dedupe by `id`: a composition referenced by the org's `compositionsBranding`
  // that is also a global preset is fetched by BOTH the by-ids (Workspace Templates)
  // and the isGlobalPreset (Arbor Templates) queries, so it appears twice in
  // `compositions`. Duplicate ids become duplicate React keys, and on every tab
  // switch the keyed-list reconciliation leaves stale nodes behind — the grid
  // visibly grows. Collapsing to the first occurrence per id keeps keys unique.
  const overlays = useMemo(() => {
    const seen = new Set<string>();
    return compositions.filter(c => {
      if (
        c.compositionType !== CompositionType.OVERLAY ||
        (c.aspectRatio && c.aspectRatio !== currentAspectRatio)
      ) {
        return false;
      }
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [compositions, currentAspectRatio]);

  const [arborOverlays, myOverlays] = useMemo(
    () =>
      overlays.reduce<[CompositionTemplate[], CompositionTemplate[]]>(
        ([arbor, mine], composition) => {
          if (composition.isGlobalPreset) arbor.push(composition);
          else mine.push(composition);
          return [arbor, mine];
        },
        [[], []],
      ),
    [overlays],
  );

  const hasMy = myOverlays.length > 0;
  const hasArbor = arborOverlays.length > 0;
  const showChips = hasMy && hasArbor;

  const [activeChip, setActiveChip] = useState<'my' | 'arbor'>('my');
  // Resolve the effective tab so we never show an empty side when only one
  // bucket has overlays.
  const effectiveChip =
    activeChip === 'my' && hasMy ? 'my' : hasArbor ? 'arbor' : 'my';
  const visibleOverlays = effectiveChip === 'my' ? myOverlays : arborOverlays;

  // AI overlays lead: they behave differently once placed, so they are what the
  // user has to recognise before picking. Section labels render only when both
  // groups are present — a lone header over the whole grid says nothing, the
  // same rule the chips above follow.
  const sections = useMemo(() => {
    const ai: CompositionTemplate[] = [];
    const plain: CompositionTemplate[] = [];
    visibleOverlays.forEach(composition => {
      (isAIOverlay(composition) ? ai : plain).push(composition);
    });
    return [
      { key: 'ai', label: 'AI Overlays', isAI: true, items: ai },
      { key: 'plain', label: 'Overlays', isAI: false, items: plain },
    ].filter(section => section.items.length > 0);
  }, [visibleOverlays]);

  const showSectionLabels = sections.length > 1;

  // Picking a card selects it; Apply commits. Resolved against the VISIBLE
  // overlays rather than held as the selection itself, so switching tabs simply
  // parks the selection (Apply disables) instead of leaving Apply armed with a
  // card the user can no longer see.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    visibleOverlays.find(composition => composition.id === selectedId) ?? null;

  return (
    <Modal isOpen closeModal={onClose}>
      <Container data-testid="overlay-graphic-picker">
        <Header>
          <DialogTitle>Add Overlay Graphic</DialogTitle>
          <DialogText>
            Pick an overlay to place over the selected words.
          </DialogText>
        </Header>

        {showChips && (
          <ChipGroup>
            <Chip
              isSelected={effectiveChip === 'my'}
              onClick={() => setActiveChip('my')}
            >
              Workspace Templates
            </Chip>
            <Chip
              isSelected={effectiveChip === 'arbor'}
              onClick={() => setActiveChip('arbor')}
            >
              Arbor Templates
            </Chip>
          </ChipGroup>
        )}

        <Body>
          {isLoading ? (
            <EmptyState>Loading overlays…</EmptyState>
          ) : visibleOverlays.length === 0 ? (
            <EmptyState>No overlay graphics available.</EmptyState>
          ) : (
            sections.map(section => (
              <Section key={section.key}>
                {showSectionLabels && (
                  <SectionLabel>{section.label}</SectionLabel>
                )}
                <Grid>
                  {section.items.map(composition => (
                    <Card
                      key={composition.id}
                      type="button"
                      onClick={() => setSelectedId(composition.id)}
                      aria-pressed={composition.id === selectedId}
                      data-testid={`overlay-graphic-card-${composition.id}`}
                    >
                      <Thumb>
                        {composition.thumbnail?.url ? (
                          <ThumbImage
                            src={composition.thumbnail.url}
                            alt={composition.label}
                            loading="lazy"
                          />
                        ) : (
                          <Placeholder>{composition.label}</Placeholder>
                        )}
                        {composition.id === selectedId && (
                          <SelectedCheckBadge
                            data-testid={`overlay-graphic-selected-${composition.id}`}
                          />
                        )}
                        {section.isAI && (
                          <AIBadge
                            data-testid={`overlay-graphic-ai-badge-${composition.id}`}
                            data-tooltip-id={PICKER_TOOLTIP_ID}
                            data-tooltip-content={AI_BADGE_TOOLTIP}
                            aria-label="AI overlay"
                          >
                            <SparkleIcon strokeColor="var(--primary)" />
                          </AIBadge>
                        )}
                      </Thumb>
                      <CardName $isSelected={composition.id === selectedId}>
                        {composition.label}
                      </CardName>
                    </Card>
                  ))}
                </Grid>
              </Section>
            ))
          )}
        </Body>

        <Footer>
          <FooterHint title={selected?.label}>
            {selected
              ? `Selected: ${selected.label}`
              : 'Select an overlay to continue.'}
          </FooterHint>
          <FooterActions>
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={!selected}
              onClick={() => selected && onSelect(selected)}
              data-testid="overlay-graphic-apply"
            >
              Apply
            </PrimaryButton>
          </FooterActions>
        </Footer>

        <Tooltip
          id={PICKER_TOOLTIP_ID}
          style={MODAL_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />
      </Container>
    </Modal>
  );
};

/**
 * The shared dialog card (elevated `--surface` + `--elevation-lg`), widened for
 * the 3-up template grid — the scaffold's widest `$size` is 580px, which crowds
 * three thumbnails.
 */
const Container = styled(DialogCard)`
  width: min(640px, 90vw);
  max-height: 80vh;
  /* The card itself never scrolls — Body does, so the header, chips and footer
     stay put and the grid can't bleed past either edge. */
  overflow: hidden;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Keeps the grid from shifting when the scrollbar appears. */
  scrollbar-gutter: stable;
`;

/** Scaffold header, inset so the title clears the shared close button. */
const Header = styled(DialogHeader)`
  padding-right: var(--spacing-xl);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const SectionLabel = styled.h3`
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
`;

const Thumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-input);
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  transition: var(--transition-normal);

  ${Card}:hover & {
    border-color: var(--primary);
  }
`;

const badgeBase = css`
  position: absolute;
  top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

/**
 * The AI marker. Redundant with the section header by design: it is the only
 * signal that stays attached to the card itself, and half the overlay artwork
 * is near-white, so the glyph needs its own dark backing to stay legible.
 */
const AIBadge = styled.span`
  ${badgeBase}
  right: 6px;
  background: color-mix(in srgb, var(--gray-900) 72%, transparent);
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: center;
`;

const CardName = styled.span<{ $isSelected: boolean }>`
  font-size: var(--text-xs);
  color: ${props =>
    props.$isSelected ? 'var(--accent)' : 'var(--foreground)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The shared `DialogFooter` right-aligns its actions; this one also carries the
 * selected overlay's name on the left, so the commit target is named in words
 * and not only by the check on a thumbnail.
 */
const Footer = styled(DialogFooter)`
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  /* Outside the scroll region (see Body), so this is a real footer rule
     separating it from the grid — not a floating band with content sliding
     under it. */
  flex-shrink: 0;
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-subtle);
`;

const FooterActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
`;

const FooterHint = styled.span`
  min-width: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  font-size: var(--text-sm);
  color: var(--text-muted);
`;
